import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import html2canvas from 'html2canvas'
import React from 'react'
import useBackendClient from '~/api/BackendClient/useBackendClient'
import { QUERY_KEYS } from '~/api/query-client-keys'
import { Slider } from '~/components/ui/slider'
import Button from './buttons/Button'
import Dialog from './Dialog'
import ToggleSwitch from './ToggleSwitch'

export interface ImageFilters {
  brightness: number
  contrast: number
  saturation: number
  hueRotate: number
  blur: number
  grayscale: number
  sepia: number
  invert: number
}

export const DEFAULT_FILTERS: ImageFilters = {
  brightness: 100,
  contrast: 100,
  saturation: 100,
  hueRotate: 0,
  blur: 0,
  grayscale: 0,
  sepia: 0,
  invert: 0,
}

interface ImageManipulationControlsProps {
  filters: ImageFilters
  onFiltersChange: (filters: ImageFilters) => void
  certificateOfAnalysis?: boolean
  onResetFilters?: () => void
  onShareURL?: () => Promise<boolean>
  plateBarcode: string
  well: string
  observationNumber: number
  isVisible?: boolean
  stitchImage?: boolean
  onStitchImageChange?: (stitched: boolean) => void
  cultureId?: string
  observationId?: string
  zoomPanState?: { scale: number; positionX: number; positionY: number }
  onImageSaved?: () => void
}

export function ImageManipulationControls({
  filters,
  onFiltersChange,
  onResetFilters,
  onShareURL,
  isVisible = true,
  plateBarcode,
  well,
  stitchImage,
  onStitchImageChange,
  observationNumber,
  cultureId,
  observationId,
  zoomPanState,
  certificateOfAnalysis,
  onImageSaved,
}: ImageManipulationControlsProps) {
  const [isExporting, setIsExporting] = React.useState(false)
  const [isCopyingURL, setIsCopyingURL] = React.useState(false)
  const [urlCopied, setUrlCopied] = React.useState(false)
  const [showOverwriteDialog, setShowOverwriteDialog] = React.useState(false)
  const [pendingImageBlob, setPendingImageBlob] = React.useState<Blob | null>(null)
  const [pendingImageMetadata, setPendingImageMetadata] = React.useState<{
    contrast: number
    brightness: number
    saturation: number
    zoomLevel: number
    panX: number
    panY: number
  } | null>(null)
  const client = useBackendClient()
  const queryClient = useQueryClient()

  // Query to check if a representative image already exists
  const { data: existingImage } = useQuery({
    queryKey: [QUERY_KEYS.REPRESENTATIVE_IMAGES, observationId],
    queryFn: async () => {
      if (!observationId) return null
      try {
        const response =
          await client.representativeImages.getRepresentativeImage(observationId)
        return response
      } catch (error: any) {
        // If it's a 404, the image doesn't exist, which is fine
        if (error?.statusCode === 404) {
          return null
        }
        // Re-throw other errors
        throw error
      }
    },
    enabled: !!observationId && certificateOfAnalysis === true,
    retry: false,
  })

  const uploadRepresentativeImageMutation = useMutation({
    mutationFn: async ({
      blob,
      metadata,
    }: {
      blob: Blob
      metadata: {
        contrast: number
        brightness: number
        saturation: number
        zoomLevel: number
        panX: number
        panY: number
      }
    }) => {
      if (!observationId) {
        throw new Error('No observation ID available')
      }
      return await client.representativeImages.uploadRepresentativeImage(
        observationId,
        blob,
        metadata,
      )
    },
    onSuccess: () => {
      if (cultureId) {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.REPRESENTATIVE_IMAGES, cultureId],
        })
      }
      // Also invalidate the query for this specific observation
      if (observationId) {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.REPRESENTATIVE_IMAGES, observationId],
        })
      }
      console.log('Representative image saved successfully')
      onImageSaved?.()
    },
    onError: error => {
      console.error('Failed to save representative image:', error)
    },
  })

  if (!isVisible) return null

  const handleFilterChange = (filterName: keyof ImageFilters, value: number) => {
    onFiltersChange({
      ...filters,
      [filterName]: value,
    })
  }

  const handleShareURL = async () => {
    if (!onShareURL) return

    setIsCopyingURL(true)
    const success = await onShareURL()
    setIsCopyingURL(false)

    if (success) {
      setUrlCopied(true)
      // Reset the "copied" message after 2 seconds
      setTimeout(() => {
        setUrlCopied(false)
      }, 2000)
    }
  }

  const handleResetFilters = () => {
    if (!onResetFilters) {
      // Fallback: just reset filters locally if no handler provided
      onFiltersChange(DEFAULT_FILTERS)
      return
    }
    onResetFilters()
  }

  /**
   * Captures the current viewport as a high-quality JPEG blob with applied filters.
   * This is the shared logic for both export and save operations.
   */
  const captureViewportAsBlob = async (): Promise<Blob> => {
    // Find the viewport wrapper that contains the transformed image
    const wrapper =
      (document.querySelector('[data-montage-export-root]') as HTMLElement | null) ??
      (document.querySelector('.react-transform-wrapper') as HTMLElement | null)

    if (!wrapper) {
      throw new Error('Viewport wrapper not found')
    }

    // Get the actual dimensions to ensure proper capture
    const rect = wrapper.getBoundingClientRect()
    const width = Math.round(rect.width)
    const height = Math.round(rect.height)

    if (width <= 0 || height <= 0) {
      throw new Error(`Viewport has zero size: width=${width}, height=${height}`)
    }

    // Use html2canvas to capture the viewport exactly as displayed
    // This will include all CSS transforms and filters
    const canvas = await html2canvas(wrapper, {
      // High quality settings
      scale: 2, // 2x resolution for high quality
      useCORS: true, // Enable CORS to request images with crossOrigin='anonymous'
      allowTaint: false, // Don't taint the canvas to ensure toBlob works with cross-origin images
      backgroundColor: '#ffffff', // White background
      logging: false, // Disable verbose logging
      imageTimeout: 15000, // 15 second timeout for image loading
      // Additional options for better rendering
      removeContainer: true, // Remove cloned DOM after rendering
      foreignObjectRendering: false, // Use standard rendering
      // Ensure the clone uses explicit dimensions equivalent to the live element.
      // This works around html2canvas's limited support for CSS aspect-ratio,
      // which otherwise collapses the layout and produces a tiny image inside
      // a mostly blank canvas.
      onclone: clonedDoc => {
        const clonedWrapper =
          (clonedDoc.querySelector(
            '[data-montage-export-root]',
          ) as HTMLElement | null) ??
          (clonedDoc.querySelector('.react-transform-wrapper') as HTMLElement | null)
        if (clonedWrapper) {
          clonedWrapper.style.width = `${width}px`
          clonedWrapper.style.height = `${height}px`
        }

        // Hide the minimap overlay in the cloned DOM so it doesn't appear
        // in the exported image while remaining visible in the live UI.
        clonedDoc.querySelectorAll('[data-montage-minimap]').forEach(el => {
          ;(el as HTMLElement).style.display = 'none'
        })
      },
    })

    // Create a new canvas to apply filters
    const filteredCanvas = document.createElement('canvas')
    filteredCanvas.width = canvas.width
    filteredCanvas.height = canvas.height
    const filteredCtx = filteredCanvas.getContext('2d')

    if (!filteredCtx) {
      throw new Error('Failed to create filtered canvas context')
    }

    // Apply the filter string to the context
    const filterString = generateFilterString(filters)
    if (filterString) {
      filteredCtx.filter = filterString
    }

    // Draw the captured canvas onto the filtered canvas
    filteredCtx.drawImage(canvas, 0, 0)

    // Convert the filtered canvas to JPEG blob with maximum quality
    return new Promise<Blob>((resolve, reject) => {
      filteredCanvas.toBlob(
        blob => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('Failed to create blob'))
          }
        },
        'image/jpeg',
        1.0, // Maximum quality
      )
    })
  }

  const exportImage = async () => {
    try {
      setIsExporting(true)

      const blob = await captureViewportAsBlob()

      // Generate filename
      const filename = `${plateBarcode}_${well}_observation_${observationNumber}.jpg`

      // Download the file
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      console.log(`Export successful: ${filename}`)
    } catch (error) {
      console.error('Export failed:', error)
    } finally {
      setIsExporting(false)
    }
  }

  const saveAsRepresentativeImage = async () => {
    if (!observationId) {
      console.error('No observation ID available to save representative image')
      return
    }

    try {
      const blob = await captureViewportAsBlob()

      // Calculate normalized coordinates for viewport independence
      let normalizedPanX = 0.5
      let normalizedPanY = 0.5

      if (zoomPanState) {
        const wrapper =
          (document.querySelector(
            '[data-montage-export-root]',
          ) as HTMLElement | null) ??
          (document.querySelector('.react-transform-wrapper') as HTMLElement | null)

        if (wrapper) {
          const containerRect = wrapper.getBoundingClientRect()
          const imageEl = wrapper.querySelector('img') as HTMLImageElement

          if (imageEl) {
            const scaledImageWidth = imageEl.naturalWidth * zoomPanState.scale
            const scaledImageHeight = imageEl.naturalHeight * zoomPanState.scale

            const maxPanX = Math.max(0, scaledImageWidth - containerRect.width)
            const maxPanY = Math.max(0, scaledImageHeight - containerRect.height)

            normalizedPanX =
              maxPanX > 0 ? (zoomPanState.positionX + maxPanX / 2) / maxPanX : 0.5
            normalizedPanY =
              maxPanY > 0 ? (zoomPanState.positionY + maxPanY / 2) / maxPanY : 0.5
          }
        }
      }

      const metadata = {
        contrast: filters.contrast,
        brightness: filters.brightness,
        saturation: filters.saturation,
        zoomLevel: zoomPanState?.scale || 1,
        panX: normalizedPanX,
        panY: normalizedPanY,
      }

      // Check if an existing image exists
      if (existingImage) {
        // Store the pending data and show the confirmation dialog
        setPendingImageBlob(blob)
        setPendingImageMetadata(metadata)
        setShowOverwriteDialog(true)
      } else {
        // No existing image, save directly
        await uploadRepresentativeImageMutation.mutateAsync({
          blob,
          metadata,
        })
      }
    } catch (error) {
      // Error handling is done in the mutation's onError callback
      console.error('Failed to save representative image:', error)
    }
  }

  const handleConfirmOverwrite = async () => {
    if (!pendingImageBlob || !pendingImageMetadata) return

    try {
      await uploadRepresentativeImageMutation.mutateAsync({
        blob: pendingImageBlob,
        metadata: pendingImageMetadata,
      })
    } finally {
      setShowOverwriteDialog(false)
      setPendingImageBlob(null)
      setPendingImageMetadata(null)
    }
  }

  const handleCancelOverwrite = () => {
    setShowOverwriteDialog(false)
    setPendingImageBlob(null)
    setPendingImageMetadata(null)
  }

  const filterConfigs = [
    {
      name: 'brightness' as const,
      label: 'Brightness',
      min: 0,
      max: 200,
      step: 1,
      unit: '%',
    },
    {
      name: 'contrast' as const,
      label: 'Contrast',
      min: 0,
      max: 300,
      step: 1,
      unit: '%',
    },
  ]

  // Controls are only available in stitched view mode
  const controlsEnabled = stitchImage !== false

  return (
    <div className='w-full flex flex-col'>
      <div className='space-y-6 py-4'>
        {stitchImage != null && onStitchImageChange && (
          <div className='pb-2 border-b border-grey-lightest'>
            <ToggleSwitch
              onLabel='Stitched View'
              offLabel='Montage View'
              isOn={stitchImage}
              onClick={() => onStitchImageChange(!stitchImage)}
            />
          </div>
        )}
        {!controlsEnabled && stitchImage != null && (
          <div className='text-sm text-grey-medium italic'>
            Image controls are only available in Stitched View mode.
          </div>
        )}
        {filterConfigs.map(config => (
          <div key={config.name} className='space-y-1'>
            <div className='flex justify-between items-center px-1'>
              <span
                className={`font-label ${controlsEnabled ? 'text-grey-dark' : 'text-grey-light'}`}
              >
                {config.label}
              </span>
              <span className='text-xs text-grey-medium min-w-[40px] text-right'>
                {filters[config.name]}
                {config.unit}
              </span>
            </div>
            <Slider
              min={config.min}
              max={config.max}
              step={config.step}
              value={[filters[config.name]]}
              onValueChange={values => handleFilterChange(config.name, values[0])}
              disabled={!controlsEnabled}
            />
          </div>
        ))}
        <div className='flex gap-2 flex-wrap'>
          <Button
            onClick={handleResetFilters}
            aria-label='Reset filters'
            type='primary'
            label='Reset'
            disabled={!controlsEnabled}
          />
          {onShareURL && (
            <Button
              onClick={handleShareURL}
              aria-label='Share URL'
              type='primary'
              label={urlCopied ? 'Copied!' : isCopyingURL ? 'Copying...' : 'Share URL'}
              disabled={isCopyingURL || !controlsEnabled}
            />
          )}
          <Button
            onClick={exportImage}
            aria-label='Export image'
            type='primary'
            label={isExporting ? 'Exporting...' : 'Export Image'}
            disabled={isExporting || !controlsEnabled}
          />
          {observationId && certificateOfAnalysis && (
            <Button
              onClick={saveAsRepresentativeImage}
              aria-label='Save as representative image'
              type='primary'
              label={
                uploadRepresentativeImageMutation.isPending
                  ? 'Saving...'
                  : 'Save as Representative'
              }
              disabled={uploadRepresentativeImageMutation.isPending || !controlsEnabled}
            />
          )}
        </div>
      </div>

      {/* Overwrite Confirmation Dialog */}
      <Dialog
        isOpen={showOverwriteDialog}
        onClose={handleCancelOverwrite}
        className='overwrite-confirmation-dialog'
      >
        <Dialog.Title>Replace Existing Representative Image?</Dialog.Title>
        <Dialog.Info>
          A representative image already exists for this observation. Saving a new
          representative image will replace the existing one. This action cannot be
          undone.
        </Dialog.Info>
        <Dialog.Footer align='right'>
          <Dialog.FooterButton
            label='Cancel'
            onClick={handleCancelOverwrite}
            type='normal'
            disabled={uploadRepresentativeImageMutation.isPending}
          />
          <Dialog.FooterButton
            label='Replace Image'
            onClick={handleConfirmOverwrite}
            type='primary'
            disabled={uploadRepresentativeImageMutation.isPending}
          />
        </Dialog.Footer>
      </Dialog>
    </div>
  )
}

// Helper function to generate CSS filter string from filter values
export function generateFilterString(filters: ImageFilters): string {
  const filterParts: string[] = []

  if (filters.brightness !== 100) {
    filterParts.push(`brightness(${filters.brightness}%)`)
  }
  if (filters.contrast !== 100) {
    filterParts.push(`contrast(${filters.contrast}%)`)
  }
  if (filters.saturation !== 100) {
    filterParts.push(`saturate(${filters.saturation}%)`)
  }
  if (filters.hueRotate !== 0) {
    filterParts.push(`hue-rotate(${filters.hueRotate}deg)`)
  }
  if (filters.grayscale !== 0) {
    filterParts.push(`grayscale(${filters.grayscale}%)`)
  }
  if (filters.sepia !== 0) {
    filterParts.push(`sepia(${filters.sepia}%)`)
  }
  if (filters.blur !== 0) {
    filterParts.push(`blur(${filters.blur}px)`)
  }
  if (filters.invert !== 0) {
    filterParts.push(`invert(${filters.invert}%)`)
  }

  return filterParts.join(' ')
}
